import type { AppUsageFeedRow, AppUsageRow } from '../../lib/database.types'

/**
 * How a department's app is used: per person and per page, over the last
 * thirty days, for the Usage section of a desk.
 *
 * The rows are a copy. The app records its employees' page views in its own
 * database and posts a daily rollup hourly (the Usage Receiver, 98h-agents
 * n8n); `v_desk_app_usage` sums the last thirty days per person and page. So
 * "as of" is the feed's last arrival, and a feed that has stopped arriving is
 * marked rather than shown as a quiet month.
 *
 * Which desk an app belongs on is the report's to say (`app_usage_feeds.department`),
 * so this names no department and no app (product rule 2).
 *
 * `daysActive` is per page, as the view computes it. A person's own figure is
 * the most days they opened any one page, which is a floor on the days they
 * used the app at all. It is labelled as that, never as days in the app.
 */

/** The sender posts hourly; three hours without one is a feed that has stopped, not a gap. */
export const USAGE_STALE_MS = 3 * 60 * 60 * 1000

export interface UsagePage {
  path: string
  views: number
  daysActive: number
  lastDay: string | null
}

export interface UsagePerson {
  userId: string
  name: string
  role: string
  views: number
  /** The most days this person opened any single page. */
  busiestPageDays: number
  lastDay: string | null
  /** Most viewed first. */
  pages: UsagePage[]
}

export interface UsageByPage {
  path: string
  views: number
  people: number
}

export interface AppUsage {
  app: string
  name: string
  asOf: string
  stale: boolean
  views: number
  /** Most views first. */
  people: UsagePerson[]
  /** Most views first. */
  pages: UsageByPage[]
}

const later = (a: string | null, b: string | null): string | null => (!a ? b : !b ? a : a > b ? a : b)

export function toAppUsage(
  rows: ReadonlyArray<AppUsageRow>,
  feeds: ReadonlyArray<AppUsageFeedRow>,
  now: number = Date.now(),
): AppUsage[] {
  return feeds
    .slice()
    .sort((a, b) => a.display_name.localeCompare(b.display_name))
    .map((feed) => {
      const people = new Map<string, UsagePerson>()
      const pages = new Map<string, { views: number; people: Set<string> }>()
      let total = 0

      for (const r of rows) {
        if (r.app !== feed.app || !r.user_id || !r.path) continue
        const views = r.views ?? 0
        const days = r.days_active ?? 0
        total += views

        let p = people.get(r.user_id)
        if (!p) {
          p = { userId: r.user_id, name: r.full_name ?? 'Unknown', role: r.role ?? '', views: 0, busiestPageDays: 0, lastDay: null, pages: [] }
          people.set(r.user_id, p)
        }
        p.views += views
        p.lastDay = later(p.lastDay, r.last_day)
        // A name or role change inside the window gives the view a second row for
        // the same person and page; it is still one page to them.
        let pg = p.pages.find((x) => x.path === r.path)
        if (pg) {
          pg.views += views
          pg.daysActive += days
          pg.lastDay = later(pg.lastDay, r.last_day)
        } else {
          pg = { path: r.path, views, daysActive: days, lastDay: r.last_day }
          p.pages.push(pg)
        }
        p.busiestPageDays = Math.max(p.busiestPageDays, pg.daysActive)

        const g = pages.get(r.path) ?? { views: 0, people: new Set<string>() }
        g.views += views
        g.people.add(r.user_id)
        pages.set(r.path, g)
      }

      const byViews = <T extends { views: number }>(key: (t: T) => string) => (a: T, b: T) =>
        b.views - a.views || key(a).localeCompare(key(b))

      const personList = [...people.values()]
      for (const p of personList) p.pages.sort(byViews<UsagePage>((x) => x.path))
      personList.sort(byViews<UsagePerson>((x) => x.name))

      const pageList = [...pages.entries()]
        .map(([path, g]) => ({ path, views: g.views, people: g.people.size }))
        .sort(byViews<UsageByPage>((x) => x.path))

      return {
        app: feed.app,
        name: feed.display_name,
        asOf: feed.received_at,
        stale: now - Date.parse(feed.received_at) > USAGE_STALE_MS,
        views: total,
        people: personList,
        pages: pageList,
      }
    })
}

/** The one line under the section's head: how much, by how many, across how much of the app. */
export function usageLine(u: AppUsage): string {
  if (u.views === 0) return 'No page views in the last 30 days'
  const n = (count: number, one: string, many: string) => `${count.toLocaleString('en-US')} ${count === 1 ? one : many}`
  return `${n(u.views, 'view', 'views')} by ${n(u.people.length, 'person', 'people')} across ${n(u.pages.length, 'page', 'pages')} · last 30 days`
}
